// Toast notifications (bottom centre on desktop, top on mobile). Polite live region; errors use
// role="alert". Timers pause while hovered or focused. At most MAX_VISIBLE at once.
import { h, icon } from '../dom.js';
import { t } from '../i18n/fr.js';

const MAX_VISIBLE = 4;
const ICONS = { info: 'info', success: 'check', warn: 'alert', error: 'alert' };

/**
 * Creates the toaster and returns the `toast(message, opts)` function (with .info/.success/.warn/
 * .error shortcuts, .clear() and .destroy()).
 * opts: { kind: 'info'|'success'|'warn'|'error', title?, timeout? (ms, 0 = sticky),
 *         action?: {label, onClick} }. `message` may be a string or an Error (its message is shown).
 * @param {{host?: HTMLElement}} [opts]
 */
export function createToaster({ host } = {}) {
  const region = h('section', { class: 'toasts', 'aria-label': t('toast.region') });
  (host || document.body).appendChild(region);
  const live = new Set();

  function show(message, opts = {}) {
    const kind = opts.kind || 'info';
    const text = message instanceof Error ? message.message : String(message ?? '');
    let timer = null;
    let remaining = opts.timeout ?? (kind === 'error' || opts.action ? 8000 : 4500);
    let startedAt = 0;
    const entry = {};

    const close = () => {
      if (!live.has(entry)) return;
      live.delete(entry);
      clearTimeout(timer);
      el.classList.add('is-leaving');
      setTimeout(() => el.remove(), 180);
    };
    const pause = () => { if (timer) { clearTimeout(timer); timer = null; remaining -= Date.now() - startedAt; } };
    const resume = () => {
      if (!remaining || remaining <= 0 || timer || !live.has(entry)) return;
      startedAt = Date.now();
      timer = setTimeout(close, Math.max(1200, remaining));
    };

    const el = h('div', {
      class: ['toast', `toast-${kind}`], role: kind === 'error' ? 'alert' : 'status',
      onMouseenter: pause, onMouseleave: resume, onFocusin: pause, onFocusout: resume,
    },
    h('span', { class: 'toast-icon', 'aria-hidden': 'true' }, icon(ICONS[kind] || 'info', { size: 16 })),
    h('div', { class: 'toast-body' },
      opts.title ? h('p', { class: 'toast-title' }, opts.title) : null,
      h('p', { class: 'toast-msg' }, text)),
    opts.action ? h('button', {
      type: 'button', class: 'toast-action',
      onClick: () => { close(); try { opts.action.onClick?.(); } catch (err) { console.error(err); } },
    }, opts.action.label) : null,
    h('button', { type: 'button', class: 'toast-close', 'aria-label': t('toast.close'), onClick: close }, icon('close', { size: 14 })));

    entry.close = close;
    live.add(entry);
    region.appendChild(el);
    while (live.size > MAX_VISIBLE) [...live][0].close();
    if (remaining > 0) resume();
    return { close };
  }

  const toast = (message, opts) => show(message, opts);
  toast.info = (m, o) => show(m, { ...o, kind: 'info' });
  toast.success = (m, o) => show(m, { ...o, kind: 'success' });
  toast.warn = (m, o) => show(m, { ...o, kind: 'warn' });
  toast.error = (m, o) => show(m, { ...o, kind: 'error' });
  toast.clear = () => { for (const e of [...live]) e.close(); };
  toast.destroy = () => { toast.clear(); region.remove(); };
  return toast;
}
